import React, { FC, Fragment, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { APP_PATHS } from '../../../electrolyse-shared/app-paths';
import { IInvite, IProject } from '../../../electrolyse-shared/interfaces';
import { useGetProject } from '../../hooks/useGetProject';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { useLoading } from '../../hooks/useLoading';
import { ScreenLayout } from '../global/ScreenLayout';
import { NotFound } from '../global/NotFound';
import { Breadcrumbs } from '../global/Breadcrumbs';
import { ContentWrapper } from '../global/ContentWrapper';
import { ProjectNav } from '../global/ProjectNav';
import { Skeleton } from '../ui/loading/Skeleton';
import { InvitesList } from '../global/InvitesList';
import { Button, EButtonStyle } from '../ui/button/Button';
import { NothingHere } from '../global/NothingHere';
import { useUser } from '../../hooks/useUser';
import { useModal } from '../../hooks/useModal';
import { InviteModal } from '../ui/modal/InviteModal';
import { RiUserUnfollowLine } from 'react-icons/ri';
import { useInvites } from '../../hooks/useInvites';

interface IProps extends RouteComponentProps {
  id?: string;
}

export const ProjectInviteScreen: FC<IProps> = ({ id }) => {
  const { setLoading, loading } = useLoading();
  const [project, setProject] = useState<IProject | null>(null);
  const [invites, setInvites] = useState<IInvite[]>([]);
  const { getProjectById } = useGetProject();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [notFound, setNotFound] = useState(false);
  const { getProjectInvites } = useInvites();
  const { user } = useUser();
  const inviteModal = useModal(props => (
    <InviteModal
      {...props}
      project={project}
      onSuccess={async () => {
        setLoading(true);
        await loadInvites();
        setLoading(false);
      }}
    />
  ));

  const loadInvites = async () => {
    const projectInvites = await getProjectInvites(id ?? '');
    setInvites(projectInvites);
  };

  useAsyncEffect(async () => {
    setLoading(true);
    try {
      const project = await getProjectById(id ?? '');
      await loadInvites();
      setProject(project);
      setBreadcrumbs([
        { title: 'Projects', path: APP_PATHS.PROJECTS },
        {
          title: project.name,
          path: APP_PATHS.PROJECT.replace(':id', id ?? ''),
        },
        {
          title: 'Members',
          path: APP_PATHS.PROJECT_INVITE.replace(':id', id ?? ''),
        },
      ]);
      if (!project) {
        setNotFound(true);
      }
      return setLoading(false);
    } catch (e) {}
    setNotFound(true);
    setLoading(false);
  }, []);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <ScreenLayout
      title={<Breadcrumbs />}
      button={
        project &&
        project?.user?._id === user?._id && (
          <div>
            <Button
              style={EButtonStyle.Accent}
              attrs={{
                onClick: () => inviteModal.open(),
              }}>
              Invite new member
            </Button>
          </div>
        )
      }>
      <ProjectNav projectId={id ?? ''} />
      <ContentWrapper>
        {loading ? (
          <Skeleton />
        ) : (
          <Fragment>
            {invites.length > 0 ? (
              <InvitesList invites={invites} />
            ) : (
              <NothingHere icon={<RiUserUnfollowLine className='h-10 w-10' />}>No one has been invited yet</NothingHere>
            )}
          </Fragment>
        )}
      </ContentWrapper>
    </ScreenLayout>
  );
};
